import { formatDistanceToNow } from "date-fns";
import { TemplatePipelineEventType } from "../../interfaces";

export const since = (iso?: string | null) =>
  iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : "—";

export const stopped = (stage?: string | null) =>
  stage ? `stopped at ${stage.replace(/_/g, " ")}` : null;

export const PROGRESS: Record<string, { label: string; bg: string; text?: "dark" }> = {
  further: { label: "Retest got further", bg: "success" },
  same: { label: "Retest failed at the same point", bg: "warning", text: "dark" },
  earlier: { label: "Retest failed earlier", bg: "danger" },
};

export const EVENT_LABEL: Record<TemplatePipelineEventType, string> = {
  [TemplatePipelineEventType.Raised]: "Raised",
  [TemplatePipelineEventType.Failed]: "Failed for a new reason",
  [TemplatePipelineEventType.Claimed]: "Claimed",
  [TemplatePipelineEventType.Released]: "Released",
  [TemplatePipelineEventType.HelpProvided]: "Help provided",
  [TemplatePipelineEventType.RetestRequested]: "Retest requested",
  [TemplatePipelineEventType.RunAnyway]: "Set to run even if not suitable",
  [TemplatePipelineEventType.Verified]: "Retest passed",
  [TemplatePipelineEventType.StillFailing]: "Retest failed",
  [TemplatePipelineEventType.RecoveredOnItsOwn]: "Passed without help",
  [TemplatePipelineEventType.Dismissed]: "Dismissed",
  [TemplatePipelineEventType.Reopened]: "Reopened",
};

export type TTemplateHelpAction = "help" | "retest" | "run-anyway" | "dismiss" | "reopen";

export const TEMPLATE_HELP_ACTIONS: Record<
  TTemplateHelpAction,
  {
    title: string;
    explainer: string;
    field: "note" | "reason";
    fieldLabel: string;
    required: boolean;
    submit: string;
    variant: string;
  }
> = {
  help: {
    title: "I've helped — retest",
    explainer:
      "Write what you did (product URL, cart URL, where the coupon field is, the account you signed in with). It is saved on the request and a capture run is queued. The request resolves itself if that run passes. To record the route itself, use the Help tab in the Checkout Extension.",
    field: "note",
    fieldLabel: "What you did",
    required: true,
    submit: "Save and retest",
    variant: "primary",
  },
  retest: {
    title: "Retest",
    explainer:
      "Queues a capture run without recording any help — after a fix on our side, or to sort a needs-triage request into a queue.",
    field: "note",
    fieldLabel: "Note (optional)",
    required: false,
    submit: "Queue retest",
    variant: "outline-primary",
  },
  "run-anyway": {
    title: "Run anyway",
    explainer:
      "Turns off the suitability check for this merchant and queues a retest. Use it when the classification is wrong, e.g. a real shop filed as a parked domain.",
    field: "note",
    fieldLabel: "Why (optional)",
    required: false,
    submit: "Run anyway",
    variant: "outline-primary",
  },
  dismiss: {
    title: "Dismiss",
    explainer:
      "Closes the request. It stays closed while runs keep failing for this same reason, and reopens if they fail for a different one.",
    field: "reason",
    fieldLabel: "Reason",
    required: true,
    submit: "Dismiss",
    variant: "outline-danger",
  },
  reopen: {
    title: "Reopen",
    explainer: "Puts the request back in the queue its last failure points to.",
    field: "note",
    fieldLabel: "Note (optional)",
    required: false,
    submit: "Reopen",
    variant: "outline-secondary",
  },
};
